import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Zap, Brain, Sparkles, Image as ImageIcon, Mic, Film, Copy, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

/**
 * B2B Models Catalog — public showcase of every model accessible through
 * Sabzy's API layer (Claude + GPT + Gemini + media). All models are backed
 * by the same Emergent LLM Key on the server side, but resellers see them
 * behind a single Sabzy API key (`sk-ant-api03-*` or `sk-sabzy_*`).
 *
 * Route: /models
 */

const CLAUDE_MODELS = [
  {
    id: "claude-opus-5",
    name: "Claude Opus 5",
    tag: "Frontier",
    tagColor: "flagship",
    context: "1M tokens",
    speed: "Slow · deepest think",
    strengths: ["Frontier reasoning", "Agentic coding", "Vision", "Long-horizon tasks"],
    useCases: "Autonomous agents · hardest research & code · enterprise analysis",
    credits: 15,
    retail: "$25 / 1M in · $125 / 1M out",
    sabzy: "50% off retail",
  },
  {
    id: "claude-sonnet-5",
    name: "Claude Sonnet 5",
    tag: "New gen",
    tagColor: "balanced",
    context: "500K tokens",
    speed: "Fast · smart",
    strengths: ["Near-Opus quality", "Fast", "Vision", "Great coding"],
    useCases: "Production assistants · coding copilots · RAG at scale",
    credits: 5,
    retail: "$5 / 1M in · $25 / 1M out",
    sabzy: "50% off retail",
  },
  {
    id: "claude-opus-4-8",
    name: "Claude Opus 4.8",
    tag: "Flagship",
    tagColor: "flagship",
    context: "200K tokens",
    speed: "Slow · deep think",
    strengths: ["Deepest reasoning", "Best code generation", "Vision + images", "Long-form writing"],
    useCases: "Complex agents · legal / financial analysis · code review · research synthesis",
    credits: 10,
    retail: "$15 / 1M in · $75 / 1M out",
    sabzy: "50% off retail",
  },
  {
    id: "claude-opus-4-7",
    name: "Claude Opus 4.7",
    tag: "Prior flagship",
    tagColor: "muted",
    context: "200K tokens",
    speed: "Slow · deep think",
    strengths: ["Advanced reasoning", "Strong coding", "Reliable"],
    useCases: "Same as Opus 4.8 · slightly cheaper alternative",
    credits: 10,
    retail: "$15 / 1M in · $75 / 1M out",
    sabzy: "50% off retail",
  },
  {
    id: "claude-sonnet-4-6",
    name: "Claude Sonnet 4.6",
    tag: "Balanced",
    tagColor: "balanced",
    context: "200K tokens",
    speed: "Fast · everyday",
    strengths: ["Great writing", "Fast responses", "Solid reasoning", "Vision"],
    useCases: "Chatbots · summarization · RAG · content · customer support",
    credits: 3,
    retail: "$3 / 1M in · $15 / 1M out",
    sabzy: "50% off retail",
  },
  {
    id: "claude-haiku-4-5-20251001",
    name: "Claude Haiku 4.5",
    tag: "Fastest",
    tagColor: "fast",
    context: "200K tokens",
    speed: "Lightning · sub-second TTFT",
    strengths: ["Ultra-fast", "Cheapest tier", "Solid quality"],
    useCases: "High-volume classification · autocomplete · search re-ranking · in-app AI",
    credits: 1,
    retail: "$1 / 1M in · $5 / 1M out",
    sabzy: "50% off retail",
  },
];

const GPT_MODELS = [
  {
    id: "gpt-6-astra",
    name: "ChatGPT Astra (GPT-6)",
    tag: "Frontier",
    tagColor: "flagship",
    context: "2M tokens",
    speed: "Deep-thinking",
    strengths: ["OpenAI's newest frontier", "2M context", "Tool use", "Vision"],
    useCases: "Hardest reasoning · long-doc analysis · full-codebase refactor · agents",
    credits: 15,
  },
  {
    id: "gpt-5.6-sol",
    name: "GPT-5.6 Sol",
    tag: "Flagship 5.6",
    tagColor: "flagship",
    context: "1M tokens",
    speed: "Deep-thinking",
    strengths: ["Frontier reasoning", "Massive context", "Tool use", "Vision"],
    useCases: "Long-doc analysis · full-codebase refactor · multi-hop agents",
    credits: 8,
  },
  {
    id: "gpt-5.6-terra",
    name: "GPT-5.6 Terra",
    tag: "General",
    tagColor: "balanced",
    context: "512K tokens",
    speed: "Fast · balanced",
    strengths: ["General purpose", "Great function calling", "Reliable"],
    useCases: "Production assistants · SaaS AI features · workflows",
    credits: 6,
  },
  {
    id: "gpt-5.6-luna",
    name: "GPT-5.6 Luna",
    tag: "Creative",
    tagColor: "creative",
    context: "256K tokens",
    speed: "Fast · playful",
    strengths: ["Creative writing", "Marketing copy", "Character voice"],
    useCases: "Content generation · social copy · storytelling · roleplay bots",
    credits: 6,
  },
  {
    id: "gpt-5.5",
    name: "GPT-5.5",
    tag: "Prior flagship",
    tagColor: "muted",
    context: "512K tokens",
    speed: "Balanced",
    strengths: ["Proven quality", "Wide compatibility"],
    useCases: "Legacy migrations · reliable general work",
    credits: 5,
  },
  {
    id: "gpt-5.4",
    name: "GPT-5.4",
    tag: "Workhorse",
    tagColor: "muted",
    context: "256K tokens",
    speed: "Fast",
    strengths: ["Fast", "Cheap-ish", "Solid general"],
    useCases: "Everyday chatbots · Q&A · content pipelines",
    credits: 3,
  },
  {
    id: "gpt-5.4-mini",
    name: "GPT-5.4 Mini",
    tag: "Cheapest",
    tagColor: "fast",
    context: "128K tokens",
    speed: "Ultra-fast",
    strengths: ["Cheapest", "Great TTFT", "Function calling"],
    useCases: "High-volume · classification · in-app AI · edge deploys",
    credits: 1,
  },
  {
    id: "gpt-5-nano",
    name: "GPT-5 Nano",
    tag: "Ultra cheap",
    tagColor: "fast",
    context: "128K tokens",
    speed: "Instant",
    strengths: ["Lowest cost", "Instant", "Bulk workloads"],
    useCases: "Tagging · extraction · moderation · millions of calls/day",
    credits: 1,
  },
];

const GEMINI_MODELS = [
  {
    id: "gemini-3.8-flash",
    name: "Gemini 3.8 Flash",
    tag: "Newest",
    tagColor: "flagship",
    context: "1M tokens",
    speed: "Very fast",
    strengths: ["Newest Flash", "Multimodal", "Great value"],
    useCases: "Production assistants · agents on a budget",
    credits: 2,
  },
  {
    id: "gemini-3.1-pro-preview",
    name: "Gemini 3.1 Pro",
    tag: "Flagship",
    tagColor: "flagship",
    context: "2M tokens",
    speed: "Deep-thinking",
    strengths: ["Longest context of any model", "Native multimodal", "Video + audio understanding", "Long-doc reasoning"],
    useCases: "Video analysis · full codebase · long PDFs · multimodal agents",
    credits: 3,
  },
  {
    id: "gemini-3.5-flash",
    name: "Gemini 3.5 Flash",
    tag: "Balanced",
    tagColor: "balanced",
    context: "1M tokens",
    speed: "Very fast",
    strengths: ["Multimodal", "Fast + accurate", "Long context"],
    useCases: "Production assistants · document Q&A · image + text apps",
    credits: 1,
  },
  {
    id: "gemini-3-flash-preview",
    name: "Gemini 3 Flash",
    tag: "Prior",
    tagColor: "muted",
    context: "1M tokens",
    speed: "Very fast",
    strengths: ["Multimodal", "Cheapest Gemini"],
    useCases: "Backward compat · high-volume light tasks",
    credits: 1,
  },
];

const MEDIA_MODELS = [
  {
    id: "nano-banana",
    name: "Nano Banana (Gemini Image)",
    tag: "Image gen",
    tagColor: "flagship",
    icon: ImageIcon,
    context: "text → 1024×1024 · 1024×1792 · 1792×1024",
    speed: "~4-6s per image",
    strengths: ["Photorealistic", "Style-consistent characters", "Fast", "Text-in-image"],
    useCases: "Marketing creatives · avatars · product mockups · blog headers",
    credits: 15,
  },
  {
    id: "gpt-image-1",
    name: "GPT Image 1 (DALL-E successor)",
    tag: "Image gen",
    tagColor: "balanced",
    icon: ImageIcon,
    context: "text + reference images → 1024–2048px",
    speed: "~8-12s per image",
    strengths: ["Follows detailed prompts", "Great composition", "Multi-image reference"],
    useCases: "Editorial illustration · IG campaigns · concept art",
    credits: 18,
  },
  {
    id: "sora-2",
    name: "Sora 2 (Video gen)",
    tag: "Video",
    tagColor: "creative",
    icon: Film,
    context: "text → up to 20s video · 1080p",
    speed: "~2-4 min per clip",
    strengths: ["Cinematic video", "Physics-aware", "Character consistency"],
    useCases: "Reels · shorts · product demos · ads",
    credits: 200,
  },
  {
    id: "openai-tts",
    name: "OpenAI TTS (natural voices)",
    tag: "Audio",
    tagColor: "fast",
    icon: Mic,
    context: "text → mp3 / wav · 9 voices",
    speed: "~1-2s per sentence",
    strengths: ["Natural intonation", "9 languages", "Streamable"],
    useCases: "Podcasts · IVR · in-app narration · audiobooks",
    credits: 5,
  },
  {
    id: "openai-whisper",
    name: "OpenAI Whisper (STT)",
    tag: "Audio",
    tagColor: "fast",
    icon: Mic,
    context: "audio → text · 100+ languages",
    speed: "~real-time",
    strengths: ["Multilingual", "Accurate", "Handles accents"],
    useCases: "Meeting notes · voice search · transcription · translation",
    credits: 4,
  },
];

const TAG_STYLES = {
  flagship: "bg-primary/15 text-primary border-primary/40",
  balanced: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  fast:     "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  creative: "bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30",
  muted:    "bg-white/5 text-muted-foreground border-white/10",
};

function ModelCard({ m }) {
  const [copied, setCopied] = useState(false);
  const snippet = `client.chat.completions.create(\n  model="${m.id}",\n  messages=[{"role":"user","content":"Hello"}]\n)`;
  const copySnippet = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      toast.success(`${m.name} snippet copied`);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const Icon = m.icon;
  return (
    <div
      data-testid={`model-card-${m.id}`}
      className="group rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur transition hover:border-primary/40 hover:bg-card"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-primary" />}
            <h3 className="text-lg font-bold leading-tight text-foreground" data-testid={`model-name-${m.id}`}>{m.name}</h3>
          </div>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{m.id}</p>
        </div>
        <Badge variant="outline" className={`shrink-0 ${TAG_STYLES[m.tagColor] || TAG_STYLES.muted}`}>{m.tag}</Badge>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-background/50 p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Context</div>
          <div className="font-semibold text-foreground">{m.context}</div>
        </div>
        <div className="rounded-md bg-background/50 p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Speed</div>
          <div className="font-semibold text-foreground">{m.speed}</div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-1">
        {m.strengths.map((s) => (
          <span key={s} className="rounded-full border border-border/50 bg-background/40 px-2 py-0.5 text-[11px] text-foreground/80">
            {s}
          </span>
        ))}
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        <span className="font-semibold text-foreground/90">Best for: </span>
        {m.useCases}
      </p>

      <div className="mb-3 flex items-center justify-between rounded-lg border border-primary/25 bg-primary/5 px-3 py-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sabzy credit cost</div>
          <div className="text-base font-black text-primary">{m.credits} <span className="text-[11px] font-medium text-muted-foreground">/ call</span></div>
        </div>
        {m.sabzy && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Retail</div>
            <div className="text-[11px] text-muted-foreground line-through">{m.retail}</div>
            <div className="text-[11px] font-bold text-primary">{m.sabzy}</div>
          </div>
        )}
      </div>

      <button
        onClick={copySnippet}
        data-testid={`model-copy-${m.id}`}
        className="flex w-full items-center justify-between rounded-lg border border-border/50 bg-background/60 px-3 py-2 text-left text-xs font-mono text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
      >
        <span className="truncate">model="{m.id}"</span>
        {copied ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 shrink-0" />}
      </button>
    </div>
  );
}

export default function Models() {
  const seo = {
    title: "AI Models Catalog · Claude · GPT · Gemini · Sabzy",
    description: "Every frontier AI model — Claude 4.8, GPT-5.6, Gemini 3.1, Nano Banana, Sora 2 — one Sabzy API key. 50% off retail. Anthropic-compatible SDK.",
  };
  const totalCount = CLAUDE_MODELS.length + GPT_MODELS.length + GEMINI_MODELS.length + MEDIA_MODELS.length;

  return (
    <div className="relative min-h-screen bg-background text-foreground grain">
      <Seo {...seo} />
      <div className="absolute inset-0 -z-10 grid-overlay opacity-20" />
      <div className="absolute left-1/2 top-0 -z-10 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-primary/10 blur-[160px]" />

      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="models-back-home">
            <Logo />
          </Link>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Link to="/chat" className="hidden hover:text-foreground sm:inline" data-testid="models-nav-chat">Chat</Link>
            <Link to="/upgrade" className="hidden hover:text-foreground sm:inline" data-testid="models-nav-plans">Plans</Link>
            <Link to="/contact" className="hidden hover:text-foreground sm:inline" data-testid="models-nav-contact">Contact</Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="models-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Link>

        {/* Hero */}
        <div className="mb-14 max-w-3xl">
          <div className="label-mono mb-3 text-primary">[ B2B API · MODELS CATALOG ]</div>
          <h1 className="text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl">
            One API key. <span className="text-primary">{totalCount} frontier models.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Every model on the Emergent LLM Key — Claude, GPT, Gemini, image, video, audio — resold through Sabzy's Anthropic-compatible endpoint. 50% off retail pricing.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full" data-testid="models-cta-get-key">
              <Link to="/contact">Get an API key →</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="models-cta-try-chat">
              <Link to="/chat">Try in chat</Link>
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-border/60 bg-card/60 p-4">
              <div className="text-2xl font-black text-primary">{CLAUDE_MODELS.length}</div>
              <div className="text-xs text-muted-foreground">Claude models</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-card/60 p-4">
              <div className="text-2xl font-black text-primary">{GPT_MODELS.length}</div>
              <div className="text-xs text-muted-foreground">GPT models</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-card/60 p-4">
              <div className="text-2xl font-black text-primary">{GEMINI_MODELS.length}</div>
              <div className="text-xs text-muted-foreground">Gemini models</div>
            </div>
            <div className="rounded-xl border border-border/60 bg-card/60 p-4">
              <div className="text-2xl font-black text-primary">{MEDIA_MODELS.length}</div>
              <div className="text-xs text-muted-foreground">Image · Audio · Video</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="claude" className="w-full">
          <TabsList className="mb-6 grid w-full grid-cols-2 sm:w-auto sm:grid-cols-4" data-testid="models-tabs">
            <TabsTrigger value="claude" data-testid="tab-claude"><Brain className="mr-1.5 h-4 w-4" />Claude</TabsTrigger>
            <TabsTrigger value="gpt" data-testid="tab-gpt"><Zap className="mr-1.5 h-4 w-4" />GPT</TabsTrigger>
            <TabsTrigger value="gemini" data-testid="tab-gemini"><Sparkles className="mr-1.5 h-4 w-4" />Gemini</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media"><ImageIcon className="mr-1.5 h-4 w-4" />Media</TabsTrigger>
          </TabsList>

          <TabsContent value="claude">
            <div className="mb-4 text-sm text-muted-foreground">Anthropic Claude — best-in-class reasoning + coding + vision. All 4 tiers available.</div>
            <div className="grid gap-5 sm:grid-cols-2">
              {CLAUDE_MODELS.map((m) => <ModelCard key={m.id} m={m} />)}
            </div>
          </TabsContent>

          <TabsContent value="gpt">
            <div className="mb-4 text-sm text-muted-foreground">OpenAI GPT — frontier general models with massive context and strong function-calling.</div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {GPT_MODELS.map((m) => <ModelCard key={m.id} m={m} />)}
            </div>
          </TabsContent>

          <TabsContent value="gemini">
            <div className="mb-4 text-sm text-muted-foreground">Google Gemini — up to 2M token context, native multimodal (image + video + audio).</div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {GEMINI_MODELS.map((m) => <ModelCard key={m.id} m={m} />)}
            </div>
          </TabsContent>

          <TabsContent value="media">
            <div className="mb-4 text-sm text-muted-foreground">Image generation, video generation, TTS, and speech-to-text — one API for every modality.</div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {MEDIA_MODELS.map((m) => <ModelCard key={m.id} m={m} />)}
            </div>
          </TabsContent>
        </Tabs>

        {/* Bottom CTA / API example */}
        <section className="mt-16 grid gap-6 rounded-2xl border border-border/60 bg-card/60 p-8 backdrop-blur md:grid-cols-2">
          <div>
            <div className="label-mono mb-2 text-primary">[ QUICK START ]</div>
            <h2 className="text-2xl font-black leading-tight sm:text-3xl">Drop-in. One base URL.</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Works with the official <span className="font-semibold text-foreground">OpenAI</span> and <span className="font-semibold text-foreground">Anthropic</span> Python / Node SDKs. No new libraries. Change the base URL — that's it.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Badge variant="outline" className="border-primary/40 text-primary">1000 credits / day</Badge>
              <Badge variant="outline" className="border-primary/40 text-primary">Midnight IST reset</Badge>
              <Badge variant="outline" className="border-primary/40 text-primary">1000 req / min</Badge>
              <Badge variant="outline" className="border-primary/40 text-primary">99.9% uptime</Badge>
            </div>
            <Button asChild size="lg" className="mt-6 rounded-full" data-testid="models-cta-bottom">
              <Link to="/contact">Book a key → DM us</Link>
            </Button>
          </div>
          <pre className="overflow-auto rounded-xl border border-border/60 bg-background/80 p-5 text-xs leading-relaxed">
{`from openai import OpenAI

client = OpenAI(
    base_url="https://sabzy.in/api/v1",
    api_key="<YOUR_SABZY_API_KEY>"   # from your Sabzy dashboard
)

reply = client.chat.completions.create(
    model="claude-opus-4-8",           # or any model above
    messages=[{"role":"user","content":"Hello!"}]
)
print(reply.choices[0].message.content)`}
          </pre>
        </section>

        <footer className="mt-16 border-t border-border/60 pt-8 text-center text-xs text-muted-foreground">
          Prices approximate · subject to Anthropic/OpenAI/Google upstream pricing changes · Sabzy is not affiliated with Anthropic, OpenAI, or Google.
        </footer>
      </main>
    </div>
  );
}
